import { BotSequenceActiveSuppressionsResponse, BotSequenceActiveSuppressionsSortKey, BotSequenceType } from "@swivl-mono/types";
import { DataTable, DataTableVariant } from "@swivl-mono/ui";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useMemo } from "react";

import { SEQUENCE_LABELS, formatExpiresIn, formatInstant, formatSubject, formatUser, pagerLabel } from "./SequenceSuppressions.Shared";

/** The columns the server sorts on — every column of this tab (ENG-3811); ids match the server's keys. */
export const SUPPRESSIONS_SORTABLE_COLUMNS = new Set<BotSequenceActiveSuppressionsSortKey>([
  "userName",
  "locationName",
  "subject",
  "suppressUntil",
  "commitmentChangeCount",
  "updatedAt"
]);

interface SuppressionsTabProps {
  sequenceType: BotSequenceType;
  data: BotSequenceActiveSuppressionsResponse | null;
  loading: boolean;
  selectedEnrollmentId: number | null;
  sorting: SortingState;
  onSortingChange: (next: SortingState) => void;
  onPageChange: (page: number) => void;
  onSelectSuppression: (enrollmentId: number | null) => void;
}

export function SuppressionsTab({
  sequenceType,
  data,
  loading,
  selectedEnrollmentId,
  sorting,
  onSortingChange,
  onPageChange,
  onSelectSuppression
}: SuppressionsTabProps): React.ReactElement {
  const suppressionRows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const page = data?.page ?? 1;
  const pageSize = data?.pageSize ?? 50;
  const lastPage = total > 0 ? Math.ceil(total / pageSize) : 1;

  // DataTable keys rows by `id`; the detail panel keys off the same enrollment id.
  const rows = useMemo(() => suppressionRows.map((row) => ({ ...row, id: row.enrollmentId })), [suppressionRows]);

  const columns: ColumnDef<(typeof rows)[number]>[] = useMemo(
    () => [
      {
        header: "User",
        accessorKey: "userName",
        enableSorting: true,
        cell: (info) => formatUser(info.getValue() as string | null, info.row.original.userId)
      },
      { header: "Location", accessorKey: "locationName", enableSorting: true, cell: (info) => (info.getValue() as string | null) ?? "—" },
      {
        header: "Subject",
        id: "subject",
        accessorKey: "leaseId",
        enableSorting: true,
        cell: (info) => formatSubject(info.row.original.leaseId, info.row.original.leadId)
      },
      {
        header: "Suppressed until",
        accessorKey: "suppressUntil",
        enableSorting: true,
        cell: (info) => (
          <span>
            {formatInstant(info.getValue() as string)}
            <span className="block text-xs text-grey-500">{formatExpiresIn(info.getValue() as string)}</span>
          </span>
        )
      },
      { header: "Times extended", accessorKey: "commitmentChangeCount", enableSorting: true },
      { header: "Last updated", accessorKey: "updatedAt", enableSorting: true, cell: (info) => formatInstant(info.getValue() as string) }
    ],
    []
  );

  return (
    <section className="flex flex-col rounded-lg border border-grey-200 bg-white">
      {rows.length === 0 && !loading ? (
        <p className="p-4 text-sm text-grey-500">No suppressions for {SEQUENCE_LABELS[sequenceType]}.</p>
      ) : (
        <DataTable
          variant={DataTableVariant.SECONDARY}
          loading={loading}
          columns={columns}
          data={rows}
          areRowsSelectable
          handleSelectCell={(id) => onSelectSuppression(id !== undefined && id !== null ? Number(id) : null)}
          selectedRowId={selectedEnrollmentId !== null ? String(selectedEnrollmentId) : null}
          sorting={sorting}
          onSortingChange={onSortingChange}
          manualConfig={{ manualSorting: true, manualPagination: true, manualFiltering: true, useCursorPagination: true }}
          paginationState={{
            hasNext: page < lastPage,
            hasPrev: page > 1,
            onNext: () => onPageChange(page + 1),
            onPrev: () => onPageChange(page - 1)
          }}
        />
      )}
      <div className="border-t border-grey-100 px-4 py-2 text-sm text-grey-600">{pagerLabel(page, pageSize, total)}</div>
    </section>
  );
}
